/** Tunable constants of the simulation. All SI units. See docs/TECHNICAL_ARCHITECTURE.md. */

/** Fixed simulation timestep (s). */
export const DT = 1 / 60;

/** Telemetry sampling: every Nth sim step => 20 Hz. */
export const SAMPLE_EVERY_STEPS = 3;
export const SAMPLE_RATE_HZ = 20;

/** Joystick magnitude below this counts as released (yield). */
export const JOYSTICK_DEADZONE = 0.15;

/** Desired heading is clamped to +/- this angle from the control frame (robot never turns back on the camera). */
export const MAX_COMMAND_HEADING = Math.PI * 0.55;
/** Mission control (ADR-022): stick directions within this angle of "up" follow the
 * road continuously; beyond it the command is LATCHED as a world heading until the
 * stick moves, so a held turn is one turn, not a spin with the rotating frame. */
export const LATCH_ANGLE = 0.6;
/** A latched stick is re-read when its direction moves by more than this (rad). */
export const LATCH_RELEASE_ANGLE = 0.2;
/** A road's frame direction flips only when the heading is this far from it (rad). */
export const FRAME_FLIP_ANGLE = (105 * Math.PI) / 180;
/** Rotate-in-place rate for a pulled-back stick (rad/s), profile-independent:
 * a deliberate human pace, not the arcade yaw limit. */
export const PIVOT_RATE = 1.6;

/** Proportional steering gain (rad/s per rad of heading error). */
export const YAW_GAIN = 4.0;

/** Internal yaw acceleration limit (rad/s^2) for physically plausible turning. */
export const YAW_ACCEL = 10.0;

/** Input smoothing time constant (s): EMA over the (post-latency) command
 * direction, absorbing small accidental joystick movements. */
export const INPUT_SMOOTHING_TAU = 0.1;

/** Pedestrian body radius (m). */
export const PED_RADIUS = 0.26;

/** Extra clearance added to collision radii for safety computations (m). */
export const SAFETY_MARGIN = 0.1;

/** TTC below this triggers EMERGENCY_STOP (s). */
export const TTC_STOP = 0.6;

/** Hysteresis: emergency stop clears when TTC exceeds this (s) on lab levels.
 * The mission uses 2.0 x TTC_STOP (safety.ts MISSION_SAFETY) so a vehicle
 * crossing a few meters ahead is ONE stop, not a stutter. */
export const TTC_CLEAR = 1.5 * TTC_STOP;

/** Hysteresis band on the SLOWDOWN->NORMAL edge (fraction of safety_ttc_s):
 * without it, TTC hovering at the threshold fires a limit cycle of
 * safety_slowdown transitions that pollutes counters and the smoothness score. */
export const TTC_SLOWDOWN_CLEAR = 1.25;

/** Free-distance margin kept from static geometry (m). */
export const STATIC_MARGIN = 0.15;

/** Emergency stop when free distance along heading drops below this (m). */
export const STATIC_STOP_DIST = 0.12;
export const STATIC_CLEAR_DIST = 0.25;

/** Ray-cast lookahead for static safety (m). */
export const SAFETY_LOOKAHEAD = 8.0;

/** Robot speed below this counts as "not moving" (yield classification, m/s). */
export const YIELD_SPEED = 0.1;

/** Lateral offset that latches a commitment (m). */
export const COMMIT_OFFSET = 0.3;

/** |joyX| threshold for the first_lateral_input event. */
export const LATERAL_INPUT_THRESHOLD = 0.4;

// ---------------------------------------------------------------------------
// Rate steering (ADR-023): the racing-game control model. The horizontal axis
// is a STEERING ANGLE, not a heading — hold it over and the robot keeps
// turning — and the vertical axis is throttle/brake. Steering is relative to
// the robot, so it needs no control frame and turning around is just "hold it
// over": no special gesture at all.

/** Full-lock yaw rate at a standstill (rad/s). */
export const STEER_RATE_MAX = 2.2;
/** Speed-sensitive steering: full lock is scaled to this at cruise and above,
 * so the robot is not twitchy at speed (every racing game does this). */
export const STEER_SPEED_MIN_SCALE = 0.42;
/** How fast the "wheel" itself can move (units/s of the [-1,1] axis). */
export const STEER_SLEW_PER_S = 5.0;
/** Below this |steer| the wheel is treated as centred (stick slop). */
export const STEER_DEADZONE = 0.06;
/** Pulling the stick back brakes at this multiple of max decel. */
export const BRAKE_STRENGTH = 1.0;
/** Forward throttle below this counts as coasting rather than driving. */
export const THROTTLE_DEADZONE = 0.08;

/** Scoring (ADR-022): dithering is measured from the player's INPUT, not from
 * the centreline offset — on a route with turns and a roundabout the offset
 * crosses the commitment band as pure geometry (the ADR-020 argument, applied
 * to the score). A sustained lateral push that flips sign within this window
 * counts as one oscillation. */
export const OSCILLATION_WINDOW_S = 2.0;
/** A lateral push must be held this long before it counts as a direction. */
export const OSCILLATION_HOLD_S = 0.2;

// ---------------------------------------------------------------------------
// Steering assistance (forgiving local motion controller).

/** Candidate heading offsets (rad) tried in order around the desired heading.
 * Symmetric list => mirror-symmetric behavior in a symmetric world. */
export const ASSIST_OFFSETS: readonly number[] = [
  0,
  Math.PI / 18, -Math.PI / 18, // ±10°
  Math.PI / 9, -Math.PI / 9, //   ±20°
  Math.PI / 6, -Math.PI / 6, //   ±30°
  Math.PI / 4, -Math.PI / 4, //   ±45°
  Math.PI / 3, -Math.PI / 3, //   ±60°
];

/** Mission (ADR-022): candidates continue out to +/-90 deg so a sideways push
 * against a curb SLIDES along it instead of wedging; the lab keeps +/-60 deg. */
export const ASSIST_OFFSETS_MISSION: readonly number[] = [
  ...ASSIST_OFFSETS,
  (5 * Math.PI) / 12, -(5 * Math.PI) / 12, // ±75°
  Math.PI / 2, -Math.PI / 2, //             ±90°
];

/** A candidate heading with at least this much free distance is accepted. */
export const ASSIST_CLEAR_DIST = 1.2;

// ---------------------------------------------------------------------------
// Stuck detection + automatic recovery.

/** Stuck when actual speed stays below this fraction of cruise speed... */
export const STUCK_SPEED_FRACTION = 0.15;
/** ...and forward progress over the window is below 0.2 robot lengths... */
export const STUCK_PROGRESS_FRACTION = 0.2;
/** ...for this long (s), while input is active and not intentionally yielding. */
export const STUCK_WINDOW_S = 1.0;
/** A pedestrian closer than this makes a stall count as yielding, not stuck (m). */
export const STUCK_PED_NEAR_DIST = 2.5;

/** First recovery: reverse this many robot lengths. */
export const RECOVERY_REVERSE_LENGTHS = 0.6;
/** Reverse creep speed during recovery (m/s). */
export const RECOVERY_REVERSE_SPEED = 0.6;
/** A second stuck within this much forward progress of the last recovery is "consecutive" (m). */
export const RECOVERY_CONSECUTIVE_PROGRESS = 1.0;
/** Safe-pose restore time penalty (s), recorded in the episode. */
export const SAFE_POSE_PENALTY_S = 2.0;

/** Safe pose: minimum clearance beyond the body to walls/obstacles (m). */
export const SAFE_POSE_STATIC_MARGIN = 0.12;
/** Safe pose: minimum distance to the pedestrian (m). */
export const SAFE_POSE_PED_DIST = 1.6;
/** Safe pose: |heading| must be within this of the destination bearing (rad). */
export const SAFE_POSE_HEADING = Math.PI / 3;
/** Safe pose is re-sampled at most this often (s). */
export const SAFE_POSE_INTERVAL_S = 0.25;

// ---------------------------------------------------------------------------
// Speed profiles. The base values come from config/robot_profile.json
// (UNVALIDATED placeholders); a profile scales them for game pacing.
// Cruise ramps to 90% in ~0.4 s => accel = 0.9/0.4 * cruise = 2.25 * cruise.

export type SpeedProfileName = "current" | "fast" | "veryFast" | "arcade";

export const SPEED_PROFILES: Record<SpeedProfileName, number> = {
  current: 1.0,
  fast: 1.5,
  veryFast: 1.8,
  /** District mission default (ADR-022): 4x the former "fast" default. */
  arcade: 6.0,
};

/** Lab levels (cal1-3, research) keep the research-calibrated default. */
export const DEFAULT_SPEED_PROFILE: SpeedProfileName = "fast";
/** The delivery mission runs at arcade pace by default (ADR-022). */
export const MISSION_SPEED_PROFILE: SpeedProfileName = "arcade";

// ---------------------------------------------------------------------------
// Player-adjustable tuning (Tweak panel). Part of the scenario identity:
// recorded with every episode and applied only at attempt start.

/** Steering sensitivity: scales the steering gain and shrinks input smoothing. */
export const TUNING_SENSITIVITY_RANGE = { min: 0.5, max: 2.0, step: 0.05 } as const;
/** Speed scale: multiplies the selected speed profile's limits. 0.25 brings
 * the arcade mission back to the pre-ADR-022 pace. */
export const TUNING_SPEED_RANGE = { min: 0.25, max: 1.5, step: 0.05 } as const;
export const TUNING_DEFAULTS = { sensitivity: 1.0, speedScale: 1.0 } as const;

/** accel = ACCEL_PER_CRUISE * effective cruise speed (0->90% in ~0.4 s). */
export const ACCEL_PER_CRUISE = 2.25;
/** decel = DECEL_PER_ACCEL * accel (braking always stronger than launch). */
export const DECEL_PER_ACCEL = 1.25;
/** Physical caps (m/s^2): the "90% cruise in 0.4 s" rule would give 16/20
 * m/s^2 at arcade pace — 2 g. Real vehicles launch and brake well under these;
 * lab profiles (<= x1.8) sit below the caps and are unaffected. */
export const ACCEL_CAP = 6.0;
export const DECEL_CAP = 9.0;
/** Corner braking (ADR-022 amendment N): the robot eases off for the bend it
 * is entering, so a fast turn is not a fight. Lookahead scales with speed;
 * cruise is scaled by 1/(1 + CORNER_GAIN * |curvature|), floored so it never
 * crawls. Mission only — lab levels have no path and are unaffected. */
export const CORNER_LOOKAHEAD_S = 1.1;
export const CORNER_GAIN = 0.5;
export const CORNER_MIN_SCALE = 0.78;

/** Throttle (ADR-022 amendment N): on the mission, how far the stick is
 * pushed sets the cruise target — a light touch crawls, a full push runs.
 * This is what a player reaches for when a corner arrives too fast, and it is
 * fully recorded (`joy_mag` is in every 20 Hz sample). Lab levels keep the
 * ADR-017 all-or-nothing cruise, so their pins are untouched. */
export const THROTTLE_MIN = 0.34;

/** Post-pass boost: cruise multiplier after a clean pedestrian pass. */
export const POST_PASS_BOOST = 1.25;

// ---------------------------------------------------------------------------
// Level layout. Alley width 2.2 m ~= 3.5-3.7x the 0.6 m robot width.

export const ALLEY_HALF_WIDTH = 1.1;
export const RESEARCH_ALLEY_LENGTH = 18;
export const RESEARCH_DESTINATION_Z = 15;
export const PED_SPAWN_Z = 16;
export const PED_WALK_SPEED = 1.2;
export const PED_REACTIVE_SLOW_SPEED = 0.8;
export const PED_REACTIVE_TTC = 1.5;

/** Cue onset when longitudinal gap (pedZ - robotZ) drops to this (m).
 * Tuned so the encounter begins within ~2 s of level start at the default
 * (fast) speed profile. */
export const CUE_GAP = 11.0;
/** Research window also ends when the gap drops to this (m). */
export const WINDOW_END_GAP = 4.0;
/** Mission research encounter (ADR-022): the robot's speed is player-tunable
 * over a 6x range, so the cue fires at a fixed CLOSING TIME instead of a
 * fixed distance — the same 3.7 s / 1.3 s the lab gaps give at the lab's
 * default pace (11 m and 4 m at 1.8 + 1.2 m/s closing). */
export const MISSION_CUE_TIME_S = 3.7;
export const MISSION_WINDOW_END_TIME_S = 1.3;
/** Lateral drift target magnitude for variants A/B (m). */
export const DRIFT_TARGET = 0.18;
/** Drift ramp duration (s, smoothstep). */
export const DRIFT_RAMP_S = 1.5;
/** Reactive phase: lateral clearance the pedestrian keeps from the robot (m).
 * Must exceed robot half-width + PED_RADIUS + margin (~0.66) for a physical pass. */
export const REACTIVE_CLEARANCE = 0.72;
/** Pedestrian lateral position limit (walls at 1.1, ped radius 0.26). */
export const PED_MAX_X = 0.84;

/** Gait sway (deterministic, seeded phase). */
export const SWAY_AMPLITUDE = 0.03;
export const SWAY_HZ = 1.8;

/** Attempt hard timeout (s). */
export const TIMEOUT_S = 60;

/** Destination reached within this distance (m). */
export const DESTINATION_RADIUS = 0.6;
/** ...or crossing the destination line within this lateral distance (m). */
export const DESTINATION_LINE_HALF_WIDTH = 1.0;

/**
 * Kerb physics, real-physics model only (ADR-025).
 *
 * A kerb is not a wall. A delivery robot that meets one squarely with some
 * speed rides up onto the pavement; one that brushes it at a shallow angle is
 * turned aside. The assisted model keeps the old behaviour — the kerb clamps
 * and the robot slides — because the lab levels and the district's pinned
 * trajectories were recorded against it.
 */

/**
 * How squarely the robot must meet a kerb to ride up it, as the sine of the
 * angle between its velocity and the kerb's normal — that is, its across-kerb
 * speed as a fraction of its own cruise. 0.45 is a shade under 27 degrees.
 *
 * An ANGLE rather than an absolute speed, because the mission runs at a 6x
 * speed profile (ADR-022): a fixed 1.2 m/s across the kerb is a 9.6 degree
 * graze at that cruise, so almost any drift would mount the kerb and the kerb
 * would stop meaning anything. As an angle the rule reads the same on every
 * speed profile — aim at the kerb and you get up it, drift into it and it
 * turns you aside.
 */
export const KERB_MOUNT_ANGLE_SIN = 0.45;

/** ...but never below this across-kerb speed (m/s), so a crawl cannot creep
 * up a kerb it would in life just rest against. */
export const KERB_MOUNT_MIN_MPS = 0.5;
/** Fraction of speed lost in the bump when the robot mounts a kerb. */
export const KERB_MOUNT_SPEED_LOSS = 0.45;
/** How fast the body settles onto a new ground height (1/s). High enough to
 * follow a kerb lurch, low enough that the drop off one is a fall, not a cut. */
export const CHASSIS_SPRING = 9.0;
/** Speed lost per metre of climb, per 60 Hz step — the momentum a wheel gives
 * up going up a step or a hump instead of along the flat. */
export const CLIMB_DRAG = 5.5;
/** Kerb face height (m) — how far the footway stands above the carriageway.
 * Matches the kerb the renderer draws, so the chassis rises by exactly what
 * the eye sees it climb. */
export const KERB_HEIGHT_M = 0.12;
/** How far past the kerb the world is still solid ground (m). */
export const PAVEMENT_REACH_M = 2.2;

/**
 * Low things on the road surface, real-physics model only (ADR-038).
 *
 * A pothole is not a bollard. Treating every obstacle as a solid box means a
 * 15 cm hole in the road stops a delivery robot dead, which is both wrong and
 * the reason speed bumps had to be modelled as scenery with no physics at all
 * — a solid speed bump is a wall.
 *
 * Anything no taller than RIDE_OVER_HEIGHT_M is driven OVER instead: the
 * robot crosses it, loses some speed to the jolt, and the run report says it
 * happened. The assisted model keeps every obstacle solid, because the lab
 * levels and the district's pinned trajectories were recorded that way.
 */

/** Obstacles no taller than this (m) are crossed rather than crashed into. */
export const RIDE_OVER_HEIGHT_M = 0.25;

/** Fraction of speed lost crossing one. */
export const RIDE_OVER_SPEED_LOSS = 0.3;

/**
 * Falling into a hole, real-physics model only (ADR-043).
 *
 * A wheel bridges a gap only up to about its own radius; past that the axle
 * drops below the lip and the wheel is in. The robot's wheel radius is the
 * measured axle-centreline height from the general-arrangement drawing,
 * 142 mm — so it clears a 14 cm crack and is swallowed by anything wider.
 *
 * That is worth stating plainly because the road-surface model got it wrong
 * the other way first: a pothole 0.9 m across was treated as something to bump
 * over, which no delivery robot on 28 cm wheels would do. It falls in.
 */

/** Wheel radius (m). From `config/robot_cameras.json` geometry_mm.axle_height. */
export const WHEEL_RADIUS_M = 0.142;

/** Half the track and half the wheelbase (m), from the same drawing:
 * track_width 687 mm, wheelbase 551 mm. The four ground contacts sit here. */
export const WHEEL_HALF_TRACK_M = 0.3435;
export const WHEEL_HALF_BASE_M = 0.2755;

/** Slowest approach that still drops the robot in rather than stopping at the lip. */
export const FALL_IN_MIN_SPEED = 0.15;

/**
 * How long the robot takes to settle into the hole (s), before the attempt is
 * called.
 *
 * Not cosmetic. Ending the run on the instant the wheel went in froze the
 * simulation mid-drop, so the footage showed the robot reach the pothole and
 * simply stop — the tip, which is the whole event, never happened on screen.
 * The robot is still moving during these six tenths of a second; it is just
 * moving downwards.
 */
export const FALL_SETTLE_S = 0.6;

/**
 * How far off centre a wheel has to enter for the robot to go over rather than
 * simply drop, as a fraction of its half-width. One side in, the other on the
 * road, is what turns a drop into a rollover.
 */
export const ROLLOVER_OFFSET_FRAC = 0.35;

/** How far out to look for the nearside kerb, and at what resolution (m). */
export const NEARSIDE_MARCH_M = 8;
export const NEARSIDE_MARCH_STEP = 0.2;

/** How long adrift from the kerb before the driver is told (s). */
export const ADRIFT_WARN_S = 4;

/** How far left of the route line counts as the wrong half (m), and how long
 * there before the driver is told (s). The margin clears the robot's own
 * half-width plus a little wander; the grace covers a deliberate lane change. */
export const WRONG_SIDE_MARGIN_M = 0.9;
export const WRONG_SIDE_WARN_S = 2;
// ---------------------------------------------------------------------------
// Living Safe Corridor (ADR-065). One visual system: follow blue, respect
// amber, wait when blue stops. Lengths in metres, times in seconds.

/** Cross-section spacing along the route. */
export const CORRIDOR_STATION_M = 2.0;
/** Longest the corridor ever reaches; also the cruise-speed length. */
export const CORRIDOR_MAX_LEN_M = 24;
/** Corridor width floor, in robot widths — it must stay a SPACE the player
 * positions themselves in, never a prescribed line. */
export const CORRIDOR_MIN_WIDTH_FACTOR = 2.4;
/** Clearance beyond the robot's half-width kept from curbs and objects. */
export const CORRIDOR_CURB_CLEAR_M = 0.3;
/** Half-width of the comfortable keep-right band on an open road. */
export const CORRIDOR_TARGET_HALF_M = 1.3;
/** Retract fast (the cap must outrun the robot's approach), extend gently
 * (resuming is an invitation, not a starting gun). */
export const CORRIDOR_RETRACT_MPS = 10;
export const CORRIDOR_EXTEND_MPS = 6;
/** How far ahead moving road users are predicted. */
export const CORRIDOR_HORIZON_S = 4;
export const CORRIDOR_PREDICT_STEP_S = 0.4;
/** The corridor ends this far short of a predicted conflict. */
export const CORRIDOR_CONFLICT_CLEAR_M = 3.0;
export const CORRIDOR_POCKET_LEN_M = 4.0;
/** The speed law reaches zero this far before the corridor's end. */
export const CORRIDOR_STOP_MARGIN_M = 1.1;
/** Extra space drawn around a priority road user's predicted path. */
export const CORRIDOR_AMBER_MARGIN_M = 0.4;
/** Minimum dwell before the state may RELAX (escalation is immediate). */
export const CORRIDOR_DWELL_S = 0.6;
/** Amber lingers briefly after its conflict clears, so it cannot strobe. */
export const CORRIDOR_AMBER_CLEAR_DELAY_S = 0.3;
/** Arrivals further apart than this are not a conflict yet. */
export const CORRIDOR_TTC_WINDOW_S = 3.0;
/** Visibility: cumulative bend within this lookahead shortens the corridor. */
export const CORRIDOR_TURN_LOOK_M = 12;
export const CORRIDOR_TURN_TIGHT_RAD = 0.5;
/** The corridor never commands below this FRACTION of cruise (amendment F,
 * tightened): it slows, it does not forbid — and it never costs the player
 * more than half their pace. Waiting at the bay is the player's own move;
 * pushing through is always available and lands in the telemetry
 * (premature_resume) instead of being prevented. */
export const CORRIDOR_FLOOR_FRAC = 0.5;

/** The road-edge (white) line is painted this far inside each kerb — and the
 * painted guidance lane's RIGHT edge sits exactly on it (play-testing: "the
 * lane should not be so near to the curb"). The renderer paints the line
 * from this same constant, so paint and lane can never drift apart. */
export const ROAD_EDGE_LINE_INSET_M = 0.32;
/** Robot d-offset counted as back-in-the-right-band after a pass. */
export const CORRIDOR_RETURN_TOL_M = 0.5;
/** Soft steering pushback starts this far from the corridor edge. */
export const CORRIDOR_EDGE_SOFT_M = 0.35;
/** How far right / left of the ROUTE the normal band may reach. The route
 * is already plotted as the correct driving line, so anchoring to it (and
 * not to the road's right edge) is what keeps the corridor in lane at
 * junctions, where the drivable region opens into the cross-street. */
export const CORRIDOR_RIGHT_MAX_M = 1.6;
export const CORRIDOR_LEFT_MAX_M = 0.9;
/** Widest the band ever gets when it must slide off the route line. */
export const CORRIDOR_SLIDE_WIDTH_M = 2.6;
/** A squeeze this tight is still offered — slowly (alleys demand it). */
export const CORRIDOR_TIGHT_CLEAR_M = 0.15;
/** The corridor cap may only RISE this fast (m/s²); drops are instant.
 * Binary slow-down toggles and cap micro-dips were the felt jerk. */
export const CORRIDOR_CAP_RISE_MPS2 = 2.0;
/** The corridor ends this far before a station a static block closes. */
export const CORRIDOR_STATIC_CLEAR_M = 1.4;
/** Lane reward (ADR-065 amendment E): a star per this many in-lane metres. */
export const LANE_STAR_EVERY_M = 5;
/** How long off the lane before the combo lets go. */
export const LANE_COMBO_GRACE_S = 1.5;
